import axios from 'axios';

/**
 * Mirrors `vue-common/utils/crudList.ts`. It is duplicated rather than imported
 * because gid-apps resolves `@gid/vue-common` to the published package, so a new
 * export only becomes available here after vue-common publishes and this repo
 * bumps the dependency. Fold the two together once that lands.
 */

/**
 * Rows per request when paging a crud list endpoint. A controller's `maxLimit`
 * silently clamps anything larger, so this must stay <= the smallest cap in use
 * (currently `maxLimit: 100` on `GET /ticket`).
 */
export const CRUD_PAGE_SIZE = 100;

interface CrudEnvelope<T> {
  data?: T[];
  pageCount?: number;
}

/**
 * `@dataui/crud` answers a bare array while a request is unpaginated, and a
 * `{ data, count, total, page, pageCount }` envelope once `page` is sent (or the
 * controller sets `alwaysPaginate`). Accept either shape.
 */
export function unwrapCrudList<T>(body: T[] | CrudEnvelope<T>): T[] {
  if (Array.isArray(body)) {
    return body;
  }
  return body?.data ?? [];
}

/**
 * Read every page of a crud list endpoint.
 *
 * A controller `maxLimit` clamps an oversized `limit`, so paging is the only way
 * to read past the cap.
 *
 * `queryForPage` must sort on a ROOT column. When the sort names a to-many
 * relation (`messages.createdAt`), the row limit applies to the *joined* rows
 * and entities are de-duplicated afterwards, so an entity whose rows straddle a
 * page boundary is returned on both pages while another is dropped entirely.
 */
export async function fetchAllCrudPages<T>(
  url: string,
  queryForPage: (page: number) => string,
  maxPages = 20,
): Promise<T[]> {
  const rows: T[] = [];

  for (let page = 1; page <= maxPages; page += 1) {
    const { data: body } = await axios.get(`${url}?${queryForPage(page)}`);
    const pageRows = unwrapCrudList<T>(body);
    rows.push(...pageRows);

    const pageCount = Array.isArray(body) ? 1 : body?.pageCount ?? 1;
    if (!pageRows.length || page >= pageCount) {
      return rows;
    }
    if (page === maxPages) {
      console.warn(
        `[fetchAllCrudPages] ${url}: stopped after ${maxPages} pages of ` +
          `${pageCount} — the result is truncated`,
      );
    }
  }

  return rows;
}
